import { GetStaticProps } from 'next';
import Link from 'next/link';

import BlogArticle from '../../../components/blog/BlogArticle';
import { getBlogPost } from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';

const ImmediateFeedbackLoopRaisesAchievement22 = () => {
  const post = getBlogPost('immediate-feedback-loop-raises-achievement-22');
  return (
    <BlogArticle post={post}>
      <p>
        {
          'Teaching in a school with 88% of students economically disadvantaged, Ms. G has dedicated years to supporting her students—creating engaging lessons, offering one-on-one help, and working with families. Despite her efforts, nearly three-quarters of her students struggled to meet grade-level expectations on her state’s English Language Arts test.'
        }
      </p>
      <figure>
        <img
          src="/blog/immediate-feedback-loop-raises-achievement-22/inline-1.png"
          alt={
            'Impact at a glance: customizable Curipod test prep used one to two times a week, and a 22 percent increase in Meets and Masters.'
          }
        />
      </figure>
      <h3>{'There had to be a way to move the needle, now.'}</h3>
      <p>
        {
          'Middle school teachers know all too well, it can take a lot to get students excited about writing. “I could be doing cartwheels to get their attention, and even that wouldn’t work,” Ms. G reflected. Then she tried Curipod. Lightbulbs switched on. “Every single lesson blew their minds,” she said. What made writing in Curipod so different from everything Ms. G had tried so far?'
        }
      </p>
      <h3>{'Three things in Curipod made the difference:'}</h3>
      <h4>{'Student agency:'}</h4>
      <p>
        {
          'Curipod can generate standards-aligned writing activities on any topic students care about. They could do a full writing lesson on Taylor Swift or the World Cup.'
        }
      </p>
      <h4>{'Instant feedback:'}</h4>
      <p>
        {
          'Curipod gave immediate AI feedback on every response, motivating students to engage more deeply. When they received feedback right after putting in effort, students were more eager to review, understand their mistakes, and improve while the writing was still fresh in their minds.'
        }
      </p>
      <h4>{'Social writing:'}</h4>
      <p>
        {
          'Writing became a shared experience as students discussed their work and feedback with each other, turning a typically solitary task into an exciting, social one.'
        }
      </p>
      <blockquote>
        {
          'In an observation, Ms. G’s principal saw it for himself: “I saw students really eager and excited to learn! Especially wanting to write. When a 6th grader is willing to write . .. I got goosebumps.”'
        }
      </blockquote>
      <p>
        {
          'Ms. G’s fellow 6th grade teachers noticed too. In February, with the state test approaching in a few short months, she led her team in using Curipod to build students’ writing skills. The 6th grade teaching team of three shared their state’s Test Prep Customizable Lessons from the Curipod library. Based on research that proves the power of feedback on writing achievement, the lessons made it possible to provide every student with an immediate feedback loop every time:'
        }
      </p>
      <ul>
        <li>
          <em>{'Students typed their response in Curipod.'}</em>
        </li>
        <li>
          <em>
            {
              'In real time, they received a score and actionable feedback aligned to their state’s writing rubric, including tips for how to improve.'
            }
          </em>
        </li>
        <li>
          <em>
            {
              'Then, students got a chance to revise their writing, all in the same class period.'
            }
          </em>
        </li>
      </ul>
      <figure>
        <img
          src="/blog/immediate-feedback-loop-raises-achievement-22/inline-2.png"
          alt={
            'Curipod feedback on a student answer: a score of 1, a note to include more specific evidence, and a suggestion to add a direct quote about the World Cup.'
          }
        />
      </figure>
      <p>
        {
          'When students got their scores and feedback in Ms. G’s classroom, it erupted in “controlled chaos”as students engaged with and talked about their feedback. Not only were students reading their feedback, they were invested in it — with students even debating when they felt they deserved a higher score.'
        }
      </p>
      <p>
        {
          'That June, state’s test results were released. The entire school was blown away. For the first time, the 6th graders earned the greatest percentage of students earning Meets or Masters — an improvement of 22% from the previous year.“This captured the 7th and 8th grade teachers’s attention,” Ms. G’s principal shared. “They all got thinking, ‘They’re closing the gap! How did they do that?’”'
        }
      </p>
      <figure>
        <img
          src="/blog/immediate-feedback-loop-raises-achievement-22/inline-3.png"
          alt={
            'Percent achieving Meets or Masters: 6th grade with Curipod test prep rose from 46 percent to 56 percent, while 7th and 8th grade without Curipod fell.'
          }
        />
      </figure>
      <blockquote>
        {
          '"Look at the scores,” Ms. G’s principal went on to say. “Because that’s the bottom line."'
        }
      </blockquote>
      <p>
        <Link href="/learning-principles" passHref>
          <a>
            {
              'Built on research-backed best practices for student engagement and learning,'
            }
          </a>
        </Link>
        {
          'Curipod uses interactive tools and artificial intelligence to help teachers provide instruction that authentically engages students in learning. For Ms. G, that’s what mattered most: “With Curipod, there is always something to spark students’ curiosity” — even during test prep.'
        }
      </p>
    </BlogArticle>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default ImmediateFeedbackLoopRaisesAchievement22;
